import { Descriptions } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { CgCloseR } from 'react-icons/cg';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  createAnswer,
  getInquiry,
  updateAnswer,
} from '../../../api/boardParentsQna.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import FileUpload from '../../../components/FileUpload';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import { getCommonCodeLabel } from '../../../lib/store';
import { IGetInquiry } from '../../../models/boardParentsQna.model';
import { RootState } from '../../../store/rootReducer';
import Radio from '../../../components/Radio';
import { getMember, getMemberChildren } from '../../../api/members.api';
import { IGetMember, IGetMemberChildren } from '../../../models/members.model';
import MemberChildrenInfo from '../../../components/homepage/member/ChildInfo';
import TableEmpty from '../../../components/common/TableEmpty';

interface IMatch {
  qnaCode: string;
}

const HomepageInquiryRead = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const match = useRouteMatch<IMatch>();
  const qnaCode = Number(match.params.qnaCode);
  const history = useHistory();
  const location = useLocation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetInquiry>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const {
    data: member,
    isLoading: memeberIsLoading,
    error: memberError,
    execute: memberExecute,
  } = useAxios<IGetMember>();

  const {
    data: memberChildren,
    isLoading: memberChildrenIsLoading,
    error: memberChildrenError,
    execute: memberChildrenExecute
  } = useAxios<IGetMemberChildren>();

  const [isAnswerMode, setIsAnswerMode] = useState(false);
  const [answer, setAnswer] = useState('');
  const [files, setFiles] = useState<any>([null]);
  const [fileinfo, setFileinfo] = useState<any>([]);
  const [answerType, setAnswerType] = useState('HP');

  useEffect(() => {
    if (qnaCode) {
      execute(getInquiry(qnaCode));
    }
  }, [qnaCode]);

  useEffect(() => {
    if (res) {
      memberExecute(getMember(res.data.questionMemCode));
      memberChildrenExecute(getMemberChildren(res.data.questionMemCode));
    }
  }, [res])

  useEffect(() => {
    if (submitRes && !isLoading) {
      execute(getInquiry(qnaCode));
      successNotification(t('common.message.save1'));
      setIsAnswerMode(false);
      setFiles([null]);
    }
  }, [submitRes, submitIsLoading]);

  const getCampusName = (value: any) => {
    const campus = campusData?.find((item: any) => item.campusCode === value);
    return campus?.campusKorName || value;
  };

  const onEditAnswer = () => {
    if (!res?.data) return;

    if (res.data.answerMemCode) {
      setAnswer(res.data.answerString);
      setAnswerType(res.data.answerType);
      setFileinfo(_.cloneDeep(res.data.answerFiles));
    }
    setIsAnswerMode(true);
  };

  const onSetFile = (index: number, file: any) => {
    const _files = [...files];
    _files[index] = file;
    setFiles(_files);
  };

  const onAddFile = () => {
    const _files = [...files];
    _files.push(null);
    setFiles(_files);
  };

  const onDeleteFile = (index: number) => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  };

  const onDeleteAttachedFile = (index: number) => {
    const _fileinfo = [...fileinfo];
    _fileinfo[index].originalName = null;
    setFileinfo(_fileinfo);
  };

  const onAnswerTypeChange = (value : 'HP' | 'PH') => {
    setAnswerType(value)
    if (value === 'HP') setAnswer('');
    if (value === 'PH') setAnswer(t('homepage.inquiry.1'));
  }

  const onSubmitAnswer = () => {
    if (!answer) {
      return infoNotification(t('homepage.inquiry.message1'));
    }

    const form = new FormData();
    form.append('answerString', answer);
    form.append('answerType', answerType);

    if (res?.data?.answerMemCode) {
      // 수정 시 파일 처리
      if (res?.data?.answerFileCode) {
        form.append('answerFileCode', res?.data?.answerFileCode);
      }

      if (fileinfo) {
        // 기존 첨부파일 있음
        let hasNewFiles = false;
        files.forEach((item: any) => {
          if (item) hasNewFiles = true;
        });

        let isAllDeleted = true;
        let hasDeleted = false;
        fileinfo.forEach((item: any) => {
          if (item.originalName) {
            isAllDeleted = false;
          } else {
            hasDeleted = true;
            form.append('answerDeleteFiles', item.fileSeq);
          }
        });

        if (hasNewFiles) form.append('answerFileStatus', 'update');
        else if (isAllDeleted) form.append('answerFileStatus', 'delete');
        else if (hasDeleted) form.append('answerFileStatus', 'update');
        else form.append('answerFileStatus', 'keep');
      } else {
        // 기존 첨부파일 없음
        if (files.length > 0 && files[0] !== null) {
          // 추가 첨부파일 있음
          form.append('answerFileStatus', 'create');
        } else {
          // 추가 첨부파일 없음
          form.append('answerFileStatus', 'keep');
        }
      }

      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('answerNewFiles', item);
        });
      }
    } else {
      // 생성 시 파일 처리
      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('files', item);
        });
      }
    }

    res?.data?.answerMemCode
      ? submitExecute(updateAnswer(qnaCode, form))
      : submitExecute(createAnswer(qnaCode, form));
  };

  const renderFileAttach = () => {
    return (
      <>
        {files.length > 0
          ? files.map((file: any, index: number) => (
              <div className="flex mb-2" key={index}>
                <FileUpload
                  file={files[index]}
                  setFile={(file: any) => onSetFile(index, file)}
                />
                <div className="comm-btn__area">
                  <Button
                    className="btn-basic gray small ml-2"
                    onClick={() => onDeleteFile(index)}
                    text={t('common.button.delete')}
                  />
                </div>
              </div>
            ))
          : null}
        <div className="comm-btn__area">
          <Button
            className="btn-basic pink-line"
            onClick={onAddFile}
            text={t('common.button.add')}
          />
        </div>
      </>
    );
  };

  return (
    <div className="content scrollbar">
      {isLoading || submitIsLoading || memeberIsLoading || memberChildrenIsLoading ? (<Loader />) : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={memberError} />
      <ErrorResponse error={memberChildrenError} />
      <SimpleHeader
        iconFile="icon-homepage-big.svg"
        parentMenu={t('common.menu.homepage')}
        currentMenu={t('homepage.inquiry.title2')}
      />

      <div className='comm-table__area rowType'>
        <Descriptions bordered column={4} size="small">
          <Descriptions.Item label={t('common.column.title')} span={4}>
            {res?.data?.title}
          </Descriptions.Item>
          {/* <Descriptions.Item label="구분">
            {getCommonCodeLabel('BR0003', res?.data?.qnaDiv || '', 'ko')}
          </Descriptions.Item> */}
          <Descriptions.Item label={t('homepage.inquiry.column2')}>
            {getCommonCodeLabel('BR0004', res?.data?.qnaStatus || '', 'ko')}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.menu.campus')}>
            {getCampusName(res?.data?.campusCode)}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column6')}>
            {res?.data?.questionMemKorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column5')}>
            {moment(res?.data?.questionDate).format('YYYY.MM.DD HH:MM:SS')}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.inquiry.column3')} span={4}>
            <div
              dangerouslySetInnerHTML={{ __html: res?.data?.content || '' }}
            />
          </Descriptions.Item>
          {res?.data?.questionFiles ? (
            <Descriptions.Item label={t('common.column.file')}>
              {res?.data?.questionFiles.map((item: any) => (
                <a
                  className="download-link mr-1"
                  href={item.fileLocation}
                  download={item.originalName}
                >
                  {item.originalName}
                </a>
              ))}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      </div>

      <div className='mt-5'>
      <MemberChildrenInfo
        memberChildrenInfo={memberChildren?.data}
        member={member?.data}
      />
      </div>

      {isAnswerMode ? (
        // 답변 등록/수정
        <div className='comm-table__area rowType'>
          <Descriptions bordered column={2} size="small" >
            <Descriptions.Item label={t('homepage.inquiry.column4')}>
              {res?.data?.answerMemCode
                ? res?.data?.answerMemKorName
                : auth?.korName}
            </Descriptions.Item>
            <Descriptions.Item label={t('homepage.inquiry.column5')}>
              {res?.data?.answerDate
                ? moment(res?.data?.answerDate).format('YYYY.MM.DD HH:MM:SS')
                : null}
            </Descriptions.Item>
            <Descriptions.Item label={t('homepage.inquiry.2')} span={2}>
              <div>
                <Radio
                  checked={answerType === 'HP' || answerType === null}
                  text={t('homepage.inquiry.3')}
                  onChange={() => onAnswerTypeChange('HP')}
                />
                <Radio
                  className="ml-3"
                  checked={answerType === 'PH'}
                  text={t('homepage.inquiry.4')}
                  onChange={() => onAnswerTypeChange('PH')}
                />
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('homepage.inquiry.column6')} span={2}>
              <textarea
                className="w-100"
                rows={9}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
              />
            </Descriptions.Item>
            {res?.data?.answerMemCode ? (
              // 첨부파일 수정
              <>
                {fileinfo ? (
                  <Descriptions.Item label={t('common.column.existFile')} span={2}>
                    {fileinfo.map((item: any, index: number) => {
                      const result = item?.originalName ? (
                        <div>
                          <a
                            className="download-link"
                            href={`${item.fileLocation}`}
                            download={item.originalName}
                          >
                            {item.originalName}
                          </a>
                          <span
                            className="ml-1 pointer"
                            onClick={() => onDeleteAttachedFile(index)}
                          >
                            <CgCloseR />
                          </span>
                        </div>
                      ) : null;
                      return result;
                    })}
                  </Descriptions.Item>
                ) : null}
                <Descriptions.Item
                  label={fileinfo ? t('common.column.additFile') : t('common.column.file')}
                  span={2}
                >
                  {renderFileAttach()}
                </Descriptions.Item>
              </>
            ) : (
              // 첨부파일 신규
              <Descriptions.Item label={t('common.column.file')} span={2}>
                {renderFileAttach()}
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>
      ) : // 등록된 답변 보기
      res?.data?.answerMemCode ? (
        <div className='comm-table__area rowType'>
          <Descriptions
            bordered
            column={2}
            size="small"
            labelStyle={{ width: '25%' }}
            contentStyle={{ width: '25%' }}
          >
            <Descriptions.Item label={t('homepage.inquiry.column4')}>
              {res?.data?.answerMemKorName}
            </Descriptions.Item>
            <Descriptions.Item label={t('homepage.inquiry.column5')}>
              {moment(res?.data?.answerDate).format('YYYY.MM.DD HH:MM:SS')}
            </Descriptions.Item>
            <Descriptions.Item label={t('homepage.inquiry.column6')} span={2}>
              <div style={{ whiteSpace: 'pre' }}>{res?.data?.answerString}</div>
            </Descriptions.Item>
            {res?.data?.answerFiles ? (
              <Descriptions.Item label={t('common.column.file')} span={2}>
                {res?.data?.answerFiles.map((item: any) => (
                  <a
                    className="download-link mr-1"
                    href={item.fileLocation}
                    download={item.originalName}
                  >
                    {item.originalName}
                  </a>
                ))}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        </div>
      ) : (
        <TableEmpty text={t('homepage.inquiry.text1')} ></TableEmpty>
      )}

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() =>
              history.push(`/homepage/inquiry${location.search}`)
            }
            text={t('common.button.list')}
          />
        </div>
        <div className="comm-btn__area right">
          {isAnswerMode ? (
            <Button
              className="btn-basic pink"
              onClick={onSubmitAnswer}
              text={t('homepage.inquiry.button1')}
              writeAuthYn={writeAuthYn}
            />
          ) : (
            <Button
              className="btn-basic pink-line"
              onClick={onEditAnswer}
              text={res?.data?.answerMemCode ? t('homepage.inquiry.button2') : t('homepage.inquiry.button3')}
              writeAuthYn={writeAuthYn}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HomepageInquiryRead;
